import { convertChannelCasing } from 'lib-shared-api-utils';
import { Channel } from 'lib-shared-types-channels';
import { useEffect } from 'react';

import {
  derivePresenceFlags,
  PresenceUpdate,
  updateMemberPresence,
} from 'store/slices/presenceOverlay.slice';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

/**
 * Converts the workload object (keyed by csid, values are channel names) into a
 * count-per-channel record that matches the `SearchUserEnriched` shape expected
 * by consumers like `getPresenceIndicator`.
 *
 * Example: `{ 123: "email", 456: "pstn_phone" }` → `{ email: 1, pstn_phone: 1
 * }`
 */
export const toWorkloadsByChannel = (
  workload: Record<string, unknown>,
): Partial<Record<Channel, number>> => {
  const counts: Partial<Record<Channel, number>> = {};

  for (const channel of Object.values(workload)) {
    if (typeof channel !== 'string') continue;
    const normalized = convertChannelCasing(channel, 'snake') as Channel;
    counts[normalized] = (counts[normalized] ?? 0) + 1;
  }

  return counts;
};

/**
 * Subscribes to presence events and dispatches updates to the Redux
 * presenceOverlay slice.
 *
 * This hook decouples presence updates from RTK Query cache mutations, allowing
 * presence data to be merged at consumption time via `useMembersWithPresence`.
 *
 * @example
 *   // In a component that needs real-time presence updates
 *   usePresenceOverlaySync();
 *   const membersWithPresence = useMembersWithPresence(members);
 */
export const usePresenceOverlaySync = (): void => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const dispatchUpdate = (memberId: string, updates: PresenceUpdate) => {
      dispatch(updateMemberPresence({ memberId, updates }));
    };

    const unsubscribeConnection = pubSub.subscribe(
      'member:connection_changed',
      (event) => {
        const presenceFlags = derivePresenceFlags(event.presence_status);
        const isOnline = event.connected && presenceFlags.is_online;

        dispatchUpdate(event.member_id, {
          is_online: isOnline,
          is_working: presenceFlags.is_working,
          simultaneous_ring: event.simultaneous_ring_on,
          working_media_types: event.available_on_media,
          custom_away_status: event.custom_away_status_id
            ? {
                custom_away_status_id: event.custom_away_status_id,
                status_updated_at: event.changed_at ?? new Date().toISOString(),
              }
            : undefined,
        });
      },
    );

    const unsubscribeAvailability = pubSub.subscribe(
      'member:availability_changed',
      (event) => {
        const presenceFlags = derivePresenceFlags(event.presence_status);

        dispatchUpdate(event.member_id, {
          is_online: presenceFlags.is_online,
          is_working: presenceFlags.is_working,
          simultaneous_ring: event.simultaneous_ring_on,
          working_media_types: event.available_on_media,
          custom_away_status: event.custom_away_status_id
            ? {
                custom_away_status_id: event.custom_away_status_id,
                status_updated_at: event.changed_at ?? new Date().toISOString(),
              }
            : undefined,
        });
      },
    );

    const unsubscribeCapacity = pubSub.subscribe(
      'member:capacity_changed',
      (event) => {
        const presenceFlags = derivePresenceFlags(event.presence_status);

        dispatchUpdate(event.member_id, {
          is_online: presenceFlags.is_online,
          is_working: presenceFlags.is_working,
          simultaneous_ring: event.simultaneous_ring_on,
          working_media_types: event.available_on_media,
          workloads_by_channel: toWorkloadsByChannel(event.workload),
          has_acw: event.conversations_in_acw.length > 0,
          custom_away_status: event.custom_away_status_id
            ? {
                custom_away_status_id: event.custom_away_status_id,
                status_updated_at: event.changed_at ?? new Date().toISOString(),
              }
            : undefined,
        });
      },
    );

    // Source: personalActions.update thunk in userModal.actions.ts
    // This is a local pubsub event (not WebSocket) fired when user edits their profile
    const unsubscribeUserModal = pubSub.subscribe(
      'userModalUserUpdated',
      (updatedUser) => {
        dispatchUpdate(updatedUser.id, {
          ...(updatedUser.name != null && { display_name: updatedUser.name }),
          ...(updatedUser.given_name != null && {
            given_name: updatedUser.given_name,
          }),
          ...(updatedUser.family_name != null && {
            family_name: updatedUser.family_name,
          }),
          ...(updatedUser.email != null && { email: updatedUser.email }),
          ...(updatedUser.phone_number != null && {
            phone_number: updatedUser.phone_number,
          }),
          ...(updatedUser.avatar_url != null && {
            avatar_url: updatedUser.avatar_url,
          }),
          ...(updatedUser.additional_emails != null && {
            additional_emails: [...updatedUser.additional_emails],
          }),
          ...(updatedUser.additional_phone_numbers != null && {
            additional_phone_numbers: [...updatedUser.additional_phone_numbers],
          }),
        });
      },
    );

    return () => {
      unsubscribeConnection();
      unsubscribeAvailability();
      unsubscribeCapacity();
      unsubscribeUserModal();
    };
  }, [dispatch]);
};
